import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../config/utilities/useAuth";
import { APIGetStops } from "../../config/utilities/APIs/stopsApi";
import { RequiredLabel } from "../../component/requiredLabel/RequiredLabel";
import TextInput from "../../component/textInput/TextInput";
import Dropdown from "../../component/dropdown/Dropdown";
import CustomTimePicker from "../../component/timePicker/TimePicker";
import "./Trips.css";
import Table from "../../component/table/Table";
import Badge from "../../component/badge/Badge";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import LoadingSpinner from "../../component/loading/LoadingSpinner";
import MapDisplayRoute from "../../component/map/MapDisplayRoute";
import { APIGenerateRoute } from "../../config/utilities/APIs/tripsApi";
import dayjs from "dayjs";
import { format, parse } from "date-fns";
import ConfirmTripModal from "../../component/modal/ConfirmTripModal";
import Text from "../../component/text/Text";
import Invalid from "../../component/textInput/img/invalid.svg";
import StopsArrayModal from "../../component/modal/StopsArrayModal";
import { getUTCOffset } from "../../config/utilities/fDate";
import { useTimezone } from "../../config/utilities/useTimezone";

export const TripForm = ({
  mode,
  existingData,
  onSubmit,
  handleReset,
  loading,
  input,
  setInput,
  error,
  setError,
  setLoading,
}) => {
  const { privilege, tripTypes } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isGenerateDisabled, setIsGenerateDisabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [initialInput, setInitialInput] = useState(null);
  const [keyUp, setKeyUp] = useState("", false);
  const [showEmptyRow, setShowEmptyRow] = useState(false);
  const [originalStopPool, setOriginalStopPool] = useState([]);
  const [stopPool, setStopPool] = useState([]);
  const [dataReady, setDataReady] = useState(false);
  const [stopPoints, setStopPoints] = useState([]);
  const [polyline, setPolyline] = useState([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [tripTypeOptions, setTripTypeOptions] = useState();
  const [lastGeneratedStartTime, setLastGeneratedStartTime] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [duplicateChildError, setDuplicateChildError] = useState(false);
  const [conflictStudents, setConflictStudents] = useState([]);
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(false);

  const { timezone } = useTimezone();

  useEffect(() => {
    const handleResize = () =>
      setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tripNameWidth = isMobileViewport ? "100%" : "45rem";
  const tripTypeWidth = isMobileViewport ? "100%" : "46.9rem";
  const startTimeWidth = isMobileViewport ? "100%" : "46.9rem";
  const mapHeight = isMobileViewport ? "18rem" : "24rem";
  const stopsTableHeight = isMobileViewport ? "24rem" : "30rem";

  const formatToHHMM = (timeString) => {
    if (!timeString) return "";

    try {
      const date = parse(timeString, "hh:mm:ss a", new Date());
      if (isNaN(date.getTime())) return "";

      return format(date, "HH:mm");
    } catch (err) {
      console.error("Failed to formatToHHMM:", err);
      return "";
    }
  };

  useEffect(() => {
    const fetchStops = async () => {
      setLoading(true);

      try {
        const response = await APIGetStops(
          undefined,
          "active",
          setLoading,
          institutionId,
        );

        if (response.status === 200) {
          const rawStops = response.data.data.stops;

          const formattedStops = rawStops.map((stop) => ({
            id: stop._id,
            name: stop.name,
            eta: "",
            location: stop.location,
            associatedChilds: stop.associatedChilds || [],
            totalChildCount: stop.totalChildCount || 0,
          }));

          setOriginalStopPool(formattedStops);
          setStopPool(formattedStops);
          setDataReady(true);

          const formattedTripTypes = tripTypes.map((type) => ({
            label: type.name,
            value: type.id,
          }));

          setTripTypeOptions(formattedTripTypes);
        }
      } catch (error) {
        console.error("Error fetching stops:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStops();
  }, []);

  const isTripFormValid = () => {
    const isValid =
      input.tripName !== "" &&
      input.tripName.length <= 80 &&
      Array.isArray(input.stopsArray) &&
      input.stopsArray.length >= 2 &&
      input.type !== "" &&
      input.startTime &&
      input.startTime.trim() !== "";

    const allETAsFilled =
      Array.isArray(input.stopsArray) &&
      input.stopsArray.slice(1).every((stop) => stop.eta && stop.eta !== "");

    const childIdCount = {};
    filteredStopsArray?.forEach((stop) => {
      stop.associatedChilds?.forEach((child) => {
        childIdCount[child.childId] = (childIdCount[child.childId] || 0) + 1;
      });
    });

    const hasDuplicateChild = Object.values(childIdCount).some(
      (count) => count > 1,
    );

    const isEdited = initialInput
      ? JSON.stringify(initialInput) !== JSON.stringify(input)
      : input.tripName.trim() !== "" ||
        (input.type && input.type.trim() !== "") ||
        (input.startTime && input.startTime.trim() !== "") ||
        (Array.isArray(input.stopsArray) && input.stopsArray.length > 0);

    const hasConflict = conflictStudents.length > 0;

    return isValid && allETAsFilled && !hasConflict && isEdited;
  };

  useEffect(() => {
    setError({
      tripName: input.tripName && input.tripName.length > 80,
      stopsArray:
        Array.isArray(input.stopsArray) && input.stopsArray.length < 2,
      type: input.type && input.type.trim() === "",
      startTime: input.startTime && input.startTime.trim() === "",
    });
  }, [input]);

  useEffect(() => {
    setIsSubmitDisabled(!isTripFormValid());
  }, [input, initialInput, conflictStudents]);

  const handleAddRowClick = () => {
    setShowEmptyRow(true);
  };

  const handleSaveRow = (newStop) => {
    setInput((prev) => {
      const updatedStops = [
        ...prev.stopsArray,
        { ...newStop, sequence: prev.stopsArray.length + 1 },
      ].map((stop, index) => ({
        ...stop,
        sequence: index + 1,
        eta: "",
      }));

      return {
        ...prev,
        stopsArray: updatedStops,
      };
    });

    setStopPool((prev) => prev.filter((s) => s.id !== newStop.id));
    setShowEmptyRow(false);
  };

  const handleCancelRow = () => {
    setShowEmptyRow(false);
  };

  const handleReorder = (newOrder) => {
    setPolyline([]);
    const reorderedStops = newOrder
      .map((item) => input.stopsArray.find((stop) => stop.id === item.id))
      .filter(Boolean)
      .map((stop, index) => ({
        ...stop,
        sequence: index + 1,
        eta: "",
      }));

    setInput((prev) => ({
      ...prev,
      stopsArray: reorderedStops,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  const handlePopoverAction = (action, rowId) => {
    if (action === "delete") {
      const removedStop = input.stopsArray.find((stop) => stop.id === rowId);

      if (removedStop) {
        const updatedStops = input.stopsArray
          .filter((stop) => stop.id !== rowId)
          .map((stop, index) => ({
            ...stop,
            sequence: index + 1,
            eta: "",
          }));

        setInput((prev) => ({
          ...prev,
          stopsArray: updatedStops,
        }));

        setStopPool((prev) => [...prev, removedStop]);
        setPolyline([]);
      }
    } else {
      const stopToEdit = input.stopsArray?.find((stop) => stop.id === rowId);

      if (stopToEdit) {
        const stopWithSelected = {
          ...stopToEdit,
          associatedChilds: stopToEdit.associatedChilds?.map((child) => ({
            ...child,
            selected: child.selected !== undefined ? child.selected : true,
          })),
        };

        setSelectedStop(stopWithSelected);
        setShowStopsModal(true);
      }
    }
  };

  useEffect(() => {
    if (
      dataReady &&
      (mode === "edit" || mode === "view") &&
      existingData &&
      originalStopPool.length > 0
    ) {
      const normalizeId = (val) =>
        typeof val === "string" ? val.trim() : String(val);

      const associatedIds =
        existingData.stopsArray?.map((stop) =>
          normalizeId(stop.stopId || stop._id || stop.id),
        ) || [];

      const filteredStopPool = originalStopPool.filter(
        (stop) => !associatedIds.includes(normalizeId(stop.id)),
      );
      setStopPool(filteredStopPool);

      const formattedStopsArray = associatedIds.map((id, index) => {
        const fullDetails = originalStopPool.find((s) => s.id === id);
        const existingStop = existingData.stopsArray?.find((s) => {
          const normalized = normalizeId(s.stopId || s._id || s.id);
          return normalized === id;
        });

        if (fullDetails) {
          const stopWithETA = {
            ...fullDetails,
            sequence: index + 1,
            ...((mode === "view" || mode === "edit") && existingStop?.eta
              ? { eta: formatToHHMM(existingStop.eta) }
              : {}),
          };
          return stopWithETA;
        }

        console.warn(`⚠️ Could not find stop with id: ${id}`);
        return {
          id,
          name: "Unknown Stop",
          eta: existingStop?.eta || "Unknown",
          sequence: index + 1,
          location: { type: "Point", coordinates: [] },
        };
      });

      const formattedInput = {
        _id: existingData._id || "",
        tripName: existingData.name || "",
        type: existingData.type || "",
        startTime: existingData.startTime || "",
        stopsArray: formattedStopsArray,
        status: existingData.status || "",
        updateScope: existingData.updateScope || "none",
      };

      setPolyline(
        existingData.tripLegsArray.map(([lng, lat]) => ({
          latitude: lat,
          longitude: lng,
        })),
      );
      setLastGeneratedStartTime(existingData.startTime);
      setInitialInput(formattedInput);
      setInput(formattedInput);
    }
  }, [dataReady, mode, existingData, originalStopPool]);

  const handleGenerateRoute = async () => {
    setPolyline([]);
    setStopPoints([]);
    setGenerateLoading(true);

    const stopsArray = input.stopsArray;
    const startTime = input.startTime;

    const formattedData = { stopsArray, startTime };

    try {
      const response = await APIGenerateRoute(
        formattedData,
        setGenerateLoading,
      );

      if (response.status === 200) {
        const { data } = response.data;

        const [startHours, startMinutes] = startTime.split(":").map(Number);
        const baseDate = new Date();
        baseDate.setHours(startHours, startMinutes, 0, 0);

        const startFormatted = dayjs(baseDate).format("HH:mm");

        let newPolyline = [];
        let newStopPoints = [];
        let updatedStopsWithETA = stopsArray.map((stop) => ({ ...stop }));
        updatedStopsWithETA[0].eta = startFormatted;

        let totalElapsedSeconds = 0;

        stopsArray.forEach((stop, index) => {
          if (index === 0) {
            const [lng, lat] = stop.location?.coordinates || [];
            if (lat != null && lng != null) {
              newStopPoints.push({ latitude: lat, longitude: lng });
            }
            return;
          }

          const dataItem = data[index - 1];
          const rawETA = dataItem?.eta ?? null;
          const coordinates = dataItem?.coordinates ?? [];

          if (Array.isArray(coordinates) && coordinates.length > 0) {
            coordinates.forEach((coord) => {
              newPolyline.push({ latitude: coord[1], longitude: coord[0] });
            });

            const lastCoord = coordinates[coordinates.length - 1];
            if (lastCoord) {
              newStopPoints.push({
                latitude: lastCoord[1],
                longitude: lastCoord[0],
              });
            }
          }

          if (rawETA != null) {
            const segmentTime = Math.ceil(rawETA);
            totalElapsedSeconds += segmentTime;

            const etaDate = new Date(
              baseDate.getTime() + totalElapsedSeconds * 1000,
            );
            const etaFormatted = dayjs(etaDate).format("HH:mm");

            updatedStopsWithETA[index].eta = etaFormatted;
          } else {
            updatedStopsWithETA[index].eta = "N/A";
          }
        });

        setPolyline(newPolyline);
        setStopPoints(newStopPoints);
        setInput((prev) => ({
          ...prev,
          stopsArray: updatedStopsWithETA,
        }));

        setStopPool((prev) =>
          prev.filter(
            (stop) => !updatedStopsWithETA.some((s) => s.id === stop.id),
          ),
        );

        setLastGeneratedStartTime(startTime);
      }
    } catch (error) {
      console.error("Error generating route:", error);
    } finally {
      setGenerateLoading(false);
    }
  };

  useEffect(() => {
    const hasEnoughStops =
      Array.isArray(input.stopsArray) && input.stopsArray.length >= 2;
    const hasStartTime = input.startTime && input.startTime.trim() !== "";

    setIsGenerateDisabled(!(hasEnoughStops && hasStartTime));
  }, [input.stopsArray, input.startTime]);

  useEffect(() => {
    const points = input.stopsArray
      .filter((stop) => stop.location?.coordinates?.length === 2)
      .map((stop) => ({
        latitude: stop.location.coordinates[1],
        longitude: stop.location.coordinates[0],
        sequence: stop.sequence,
        name: stop.name,
      }));

    setStopPoints(points);
  }, [input.stopsArray]);

  useEffect(() => {
    if (input.startTime && input.startTime !== lastGeneratedStartTime) {
      setPolyline([]);
      setStopPoints([]);

      const updatedStops = input.stopsArray.map((stop, index) => ({
        ...stop,
        sequence: index + 1,
        eta: "",
      }));

      setInput((prev) => ({
        ...prev,
        stopsArray: updatedStops,
      }));
    }
  }, [input.startTime, lastGeneratedStartTime]);

  // Check for child duplicates
  const filteredStopsArray = useMemo(() => {
    return input.stopsArray?.map((stop) => {
      const tripType = input.type?.toLowerCase?.();

      return {
        ...stop,
        associatedChilds:
          stop.associatedChilds?.filter((child) => {
            if (child.selected === false) return false;

            if (!tripType || tripType === "am") return true;

            const matchingStops = child.associatedStop?.filter(
              (as) => as.stopId === stop.id,
            );

            if (!matchingStops || matchingStops.length === 0) return false;

            return matchingStops.some((as) => {
              const session =
                as.tripSession != null &&
                as.tripSession.toString().trim() !== ""
                  ? as.tripSession.toLowerCase()
                  : "none";

              return session === "none" || session === tripType;
            });
          }) || [],
      };
    });
  }, [input.stopsArray, input.type]);

  useEffect(() => {
    const childIdCount = {};
    filteredStopsArray?.forEach((stop) => {
      stop.associatedChilds?.forEach((child) => {
        childIdCount[child.childId] = (childIdCount[child.childId] || 0) + 1;
      });
    });
    const hasDuplicateChild = Object.values(childIdCount).some(
      (count) => count > 1,
    );
    setDuplicateChildError(hasDuplicateChild);

    // Find students with multiple stops where none match trip type
    const tripType = input.type?.toLowerCase?.();
    const childStopsMap = {};
    const childInfoMap = {};

    // Helper function to get the session for a child at a specific stop
    const getChildSession = (child, stop) => {
      const matchingStops = child.associatedStop?.filter(
        (as) => as.stopId === stop.id,
      );

      if (!matchingStops || matchingStops.length === 0) return null;

      // Get the session from the first matching stop
      const session = matchingStops[0]?.tripSession;
      return session != null && session.toString().trim() !== ""
        ? session.toLowerCase()
        : "none";
    };

    // Build map of which stops each child appears in (from original input, not filtered)
    input.stopsArray?.forEach((stop) => {
      stop.associatedChilds?.forEach((child) => {
        if (child.selected !== false) {
          if (!childStopsMap[child.childId]) {
            childStopsMap[child.childId] = [];
            childInfoMap[child.childId] = {
              firstName: child.firstName,
              lastName: child.lastName,
            };
          }
          childStopsMap[child.childId].push({ stop, child });
        }
      });
    });

    // Find students with conflicts based on the rules (works for both AM and non-AM trips):
    // 1. If there are multiple exact matches (tripSession === tripType) -> conflict
    // 2. If there are no exact matches but multiple "none" -> conflict
    // 3. Otherwise no conflict (one exact match, or one "none" as fallback)
    const conflicts = [];
    Object.keys(childStopsMap).forEach((childId) => {
      const stopChildPairs = childStopsMap[childId];
      if (stopChildPairs.length >= 2 && tripType) {
        let exactMatchCount = 0;
        let noneMatchCount = 0;

        stopChildPairs.forEach(({ stop, child }) => {
          const session = getChildSession(child, stop);
          if (session === tripType) {
            exactMatchCount++;
          } else if (session === "none") {
            noneMatchCount++;
          }
        });

        // Conflict if:
        // - Multiple exact matches (e.g., multiple stops with "am" for AM trip, or "pm" for PM trip)
        // - No exact matches but multiple "none" (e.g., multiple stops with "none" when no exact match exists)
        const hasConflict =
          exactMatchCount > 1 || (exactMatchCount === 0 && noneMatchCount > 1);

        if (hasConflict) {
          conflicts.push(childInfoMap[childId]);
        }
      }
    });

    setConflictStudents(conflicts);
  }, [filteredStopsArray, input.stopsArray, input.type]);

  useEffect(() => {
    if (!input?.stopsArray) return;

    const resetStops = input.stopsArray.map((stop) => ({
      ...stop,
      associatedChilds: stop.associatedChilds?.map((child) => ({
        ...child,
        selected: true,
      })),
    }));

    setInput((prev) => ({
      ...prev,
      stopsArray: resetStops,
    }));
  }, [input.type]);

  const handleRowChange = (rowId, field, value) => {
    setInput((prev) => ({
      ...prev,
      stopsArray: prev.stopsArray.map((row) =>
        row.id === rowId ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleSubmit = () => {
    if (mode === "edit") {
      setShowConfirmationModal(true);
    } else {
      onSubmit();
    }
  };

  // Helper to edit the student list
  const editStudentList = (updatedStop) => {
    if (!updatedStop) return;

    setInput((prev) => ({
      ...prev,
      stopsArray: prev.stopsArray.map((stop) => {
        if (stop.id !== updatedStop.id) return stop;

        const mergedChildren = stop.associatedChilds.map((origChild) => {
          const updatedChild = updatedStop.associatedChilds.find(
            (c) => c.childId === origChild.childId,
          );
          return updatedChild
            ? { ...origChild, selected: updatedChild.selected }
            : origChild;
        });

        return { ...stop, associatedChilds: mergedChildren };
      }),
    }));

    setShowStopsModal(false);
  };

  if (loading) {
    return (
      <div className="content-loading-container">
        <LoadingSpinner
          className="loading-spinner"
          size="large"
          color="#ffb502"
        />
      </div>
    );
  }

  // Students = unique childIds across stops (mirrors the per-stop "Students" column).
  const totalStudentCount = new Set(
    filteredStopsArray?.flatMap((stop) =>
      (stop.associatedChilds || []).map((child) => child.childId),
    ),
  ).size;
  const stopCount = filteredStopsArray?.length || 0;

  const countBadge = (label, count) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.375rem",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <Badge text={String(count)} background="#F2F4F7" color="#344054" />
    </span>
  );

  // Desktop: counts sit in the column headers. Mobile: plain headers + a badge bar below the table.
  const headers = [
    { key: "sequence", name: "", sortable: false },
    {
      key: "name",
      name: isMobileViewport ? "Stop Name" : countBadge("Stop Name", stopCount),
      sortable: false,
    },
    {
      key: "children",
      name: isMobileViewport
        ? "Students"
        : countBadge("Students", totalStudentCount),
      sortable: false,
    },
    { key: "eta", name: `ETA (${getUTCOffset(timezone)})`, sortable: false },
  ];

  return (
    <>
      <div className="name-form-container">
        <div className="input-label">
          {<RequiredLabel>Trip Name</RequiredLabel>}
        </div>
        <div className="input-container">
          <TextInput
            placeholder={mode === "view" ? input.tripName : "Enter Trip Name"}
            setInput={setInput}
            input={mode === "view" ? "" : input}
            name="tripName"
            disable={mode === "view"}
            type="text"
            error={error.tripName}
            setError={setError}
            errorMessage="Stop name should not exceed 80 characters"
            setKeyUp={setKeyUp}
            onChangeFunction={handleInputChange}
            width={tripNameWidth}
            showCharCount
            maxLength={80}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Trip Type</RequiredLabel>
          <div className="input-description">
            Select whether the type of trip you would like to create. This will
            help in organizing and managing the trip schedule effectively.
            <br></br>
            <br></br>
            <strong>Note:</strong> The trip type determines which stops a
            student is assigned to. For instance, if a student belongs to
            different stops for a PM or ECA trip, as defined in the Stops
            module, their assignment will reflect this here. Make sure to review
            the stop assignments based on the selected trip type to ensure
            accuracy. Trip schedules follow the institution’s timezone.
          </div>
        </div>
        <div style={{ marginTop: "2rem" }} className="input-container">
          <Dropdown
            disabled={mode === "view" || mode === "edit"}
            options={tripTypeOptions}
            name="type"
            placeholder="Select a type..."
            onChangeFunction={handleInputChange}
            input={input}
            width={tripTypeWidth}
          />
        </div>
      </div>
      <div className="trip-start-time-form-container">
        <div className="input-label">
          {<RequiredLabel>Start Time</RequiredLabel>}
          <div className="input-description">
            Set the time when the trip is scheduled to begin its journey from
            the first stop.
          </div>
        </div>
        <div className="input-container">
          <CustomTimePicker
            value={input.startTime}
            onChange={(val, name) =>
              setInput((prev) => ({
                ...prev,
                [name]: val || "",
              }))
            }
            name="startTime"
            error={mode === "view" ? null : error.startTime}
            errorMessage=""
            setError={setError}
            placeholder="Select a Time"
            disable={mode === "view"}
            width={startTimeWidth}
          />
        </div>
      </div>
      <div className="name-form-container">
        <div className="input-label">
          <RequiredLabel>Stop Sequence</RequiredLabel>
          <div className="input-description" style={{ paddingBottom: "1rem" }}>
            Manage the sequence of stops for this trip. You can add, remove, or
            rearrange stops as needed. The route will update on the map upon
            generation.
            <br></br>
            <br></br>
            <strong>Note:</strong> Before confirming the creation or edit of
            this trip, you must generate the route. This will calculate the
            exact path and estimated arrival times (ETAs) for each stop. A valid
            start time must also be provided in order to generate the route.
            <br></br>
            <br></br>
            <strong>Important:</strong> Any changes to the stop sequence —
            including adding, removing, or reordering stops — will require
            regenerating the route. Plan carefully before making adjustments.
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            gap: "1rem",
            marginRight: isMobileViewport ? 0 : "1rem",
          }}
        >
          <div className="input-container" style={{ height: mapHeight }}>
            <MapDisplayRoute polyline={polyline} stopPoints={stopPoints} />
          </div>
          <div
            className="input-container"
            style={{
              flex: 1,
              paddingBottom: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <Table
              data={filteredStopsArray || []}
              searchText={"Search by stop name..."}
              headers={headers}
              showEmptyHeader={role !== "VIEWER"}
              showFilter={true}
              showSearchBar={false}
              handlePopoverAction={handlePopoverAction}
              onClick={handleAddRowClick}
              module={"stopsArray"}
              loading={loading}
              showTableAction={
                role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
              }
              showEmptyRow={showEmptyRow}
              onSave={handleSaveRow}
              onCancel={handleCancelRow}
              optionData={stopPool}
              emptyStateSize="small"
              draggable={mode !== "view"}
              onReorder={handleReorder}
              onRowChange={handleRowChange}
              height={stopsTableHeight}
              showPagination={false}
            />

            {/* Table scrolls horizontally on mobile, so header counts would scroll
                off-screen — surface them in a badge bar under the table instead. */}
            {isMobileViewport && filteredStopsArray?.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "1.25rem",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: "#475467",
                }}
              >
                {countBadge("Stops", stopCount)}
                {countBadge("Students", totalStudentCount)}
              </div>
            )}

            {conflictStudents.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                  maxWidth: "100%",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start" }}>
                  <img
                    src={Invalid}
                    style={{
                      marginRight: "0.3rem",
                      flexShrink: 0,
                      marginTop: "0.125rem",
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: "#f43f5e",
                        fontSize: "0.875rem",
                        fontFamily: "Inter",
                        lineHeight: "1.25rem",
                        height: "auto",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      Some students appear in multiple stops, causing a
                      conflict. This occurs when a student has multiple stops
                      with the same Trip Session, or multiple stops all set to
                      'All Trip Sessions'. Please update the Student Trip
                      Session assignments in the Stops module to resolve this.
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    marginLeft: "1.5rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.25rem",
                  }}
                >
                  <Text
                    text="Students with conflicts:"
                    color="#f43f5e"
                    size="0.875rem"
                    weight="600"
                  />
                  <div
                    style={{
                      maxHeight: "12rem",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.25rem",
                      paddingRight: "0.5rem",
                    }}
                  >
                    {conflictStudents.map((student, index) => (
                      <Text
                        key={index}
                        text={`• ${student.firstName} ${student.lastName}`}
                        color="#f43f5e"
                        size="0.875rem"
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {mode !== "view" && (
              <div
                style={{
                  display: "flex",
                  justifyContent: isMobileViewport ? "center" : "flex-end",
                }}
              >
                <PrimaryButton
                  width="10rem"
                  disable={isGenerateDisabled || conflictStudents.length > 0}
                  label={"Generate Route"}
                  onClickFunction={handleGenerateRoute}
                  loading={generateLoading}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="button-container-edit">
        <SecondaryButton
          width="6rem"
          label="Cancel"
          imageIcon={false}
          onClickFunction={handleReset}
        />
        {mode !== "view" && (
          <PrimaryButton
            width="6rem"
            disable={isSubmitDisabled}
            label={mode === "create" ? "Create" : "Save"}
            onClickFunction={handleSubmit}
            loading={loading}
          />
        )}
      </div>
      <StopsArrayModal
        selectedStop={selectedStop}
        isOpen={showStopsModal}
        onClose={() => setShowStopsModal(false)}
        onConfirm={editStudentList}
        tripType={input.type}
        input={input.stopsArray}
      />
      <ConfirmTripModal
        initialInput={initialInput}
        input={input}
        setInput={setInput}
        isOpen={showConfirmationModal}
        loading={loading}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={onSubmit}
      />
    </>
  );
};
